import Css from "./style.module.scss";

import defaultBusinessLogo from "assets/business.svg";
import quickBooksAccountantLogo from "assets/quickBooksAccountant.svg";

import * as Icons from "@phosphor-icons/react";
import { Input } from "nlib/ui";
import { checkIsBusinessUser } from "selectors/user";
import { getActiveOrganization, getProjectName } from "selectors/organizations";
import { getSelectedBusinessId, getUserBusinessesData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Arrays from "utils/Arrays";
import BusinessStatus from "../BusinessStatus";
import Countries from "const/Countries";
import DataStatuses from "const/DataStatuses";
import IntegrationServices from "const/IntegrationServices";
import Pages from "nlib/pages/Pages";
import Platforms from "utils/Platforms";
import React, { useCallback, useMemo, useState } from "react";
import Select from "nlib/ui/Select";
import UiActions from "actions/UiActions";
import UiRoutes from "const/UiRoutes";
import classNames from "classnames";

const ADD_NEW_BUSINESS_ID = "+";

const MIN_BUSINESSES_COUNT_FOR_SEARCH = 10;

const getStatus = (route) => {
  switch (route) {
    case UiRoutes.TRANSACTIONS:
      return DataStatuses.NEED_REACTION;

    case UiRoutes.DOCUMENTS:
      return DataStatuses.TO_REVIEW;

    default:
      return undefined;
  }
};

const BusinessSelector = ({ disabled, className }) => {
  const dispatch = useDispatch();

  const history = useHistory();

  const { pathname } = useLocation();

  const projectName = useSelector(getProjectName);

  const businessesData = useSelector(getUserBusinessesData);

  const businessUser = useSelector(checkIsBusinessUser);

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const { countryCode } = useSelector(getActiveOrganization);

  const { uiTexts } = useSelector(getTextsData);

  const czCountry = countryCode === Countries.CZ;

  const [searchString, setSearchString] = useState("");

  const handleSearchChange = useCallback((value) => {
    setSearchString(value);
  }, []);

  const options = useMemo(() => {
    const searchStringNormalized = searchString.trim().toLowerCase();

    const mainOptions = businessesData.map(({ id, name, extraData = {}, settings = {} }) => {
      const defaultBusiness = { logo: defaultBusinessLogo, label: projectName };

      const found = !czCountry && IntegrationServices.getByValue(extraData.integrationService);

      const { logo: serviceLogo, label: serviceName } = found || defaultBusiness;

      const quickBooksAccountantBusiness = !!extraData.quickBooksAccountantClients;

      const serviceTooltip = quickBooksAccountantBusiness
        ? IntegrationServices.QUICK_BOOKS.altLabel
        : serviceName;

      return {
        value: id,
        hidden: searchStringNormalized && !`${name}`.toLowerCase().includes(searchStringNormalized),
        label: (
          <div className={Css.listItem}>
            <div
              className={Css.logo}
              data-tooltip={serviceTooltip}
              data-tooltip-position="right">
              <img
                alt={serviceTooltip}
                src={quickBooksAccountantBusiness ? quickBooksAccountantLogo : serviceLogo} />
            </div>
            {!businessUser && (
              <BusinessStatus
                iconOnly
                className={Css.businessStatus}
                extraData={extraData}
                settings={settings} />
            )}
            <div className={Css.name} title={name}>{name}</div>
          </div>
        )
      };
    });

    return [
      !businessUser && !searchStringNormalized && !Platforms.checkIsTouchDevice() && {
        value: ADD_NEW_BUSINESS_ID,
        label: (
          <div className={Css.addNew}>
            <Icons.Plus className={Css.plus} weight="bold" />
            <span>{uiTexts.addNewBusiness}</span>
          </div>
        )
      },
      ...mainOptions,
      searchStringNormalized && mainOptions.every(({ hidden }) => hidden) && {
        value: null,
        disabled: true,
        label: <span>{uiTexts.noResultsFound}</span>
      }
    ].filter(Boolean);
  }, [searchString, businessesData, businessUser, uiTexts, projectName, czCountry]);

  const handleChange = useCallback((businessId) => {
    if (businessId === ADD_NEW_BUSINESS_ID) {
      dispatch(UiActions.showAddBusinessWindow());
    } else {
      const { route = "", restPath } = Pages.getCurrentRouteInfo(pathname);

      const businessData = Arrays.findById(businessesData, businessId);

      const rpaMode = businessData?.extraData?.uncategorizedRpaEnabled !== undefined;

      if (rpaMode && route === UiRoutes.TRANSACTIONS) {
        history.push(`/${businessId}${route}`);
      } else {
        const status = getStatus(route);

        history.push(`/${businessId}${route}${
          restPath.length ? `/${restPath.join("/")
          }` : ""}${
          status ? `?status=${status}` : ""
        }`);
      }
    }
  }, [pathname, businessesData, dispatch, history]);

  const handleSelectToggle = useCallback(() => {
    setSearchString("");
  }, []);

  if (!businessesData.length) return null;

  return (
    <Select
      disableSelected
      centered={false}
      disabled={disabled}
      className={classNames(Css.businessSelector, className)}
      dropDownClassName={Css.businessSelectorDropDown}
      placeholder={uiTexts.selectBusinesses}
      value={selectedBusinessId}
      options={options}
      prepend={(businessesData.length >= MIN_BUSINESSES_COUNT_FOR_SEARCH) && (
        <Input
          autoFocus
          className={Css.businessSearch}
          placeholder={uiTexts.searchBusiness}
          value={searchString}
          onChange={handleSearchChange} />
      )}
      onToggle={handleSelectToggle}
      onChange={handleChange} />
  );
};

export default React.memo(BusinessSelector);
